/**
 * V70 Phase C — 공용 대진표 트리 뷰 (Admin + 참가자 재사용)
 *
 * 지시 §10 모바일 가로 스크롤 + 본인 하이라이트 (UID 미노출).
 * 라운드별 column, 매치 cell click → onMatchClick (Admin 세트 입력용).
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TeamMembersPopover, TeamNameTrigger, useBracketTeamMembers, type OpenTeam } from './TeamMembersPopover';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../../firebase/utils';
import {
  PublicBracketView, PublicMatchView, PublicParticipantView,
  roundLabelOf, tsToMs,
} from './bracketTypes';
import { DARK } from './tournamentDarkTheme';

/**
 * V70-M — 색 팔레트. 'light' = 기존 값 그대로 (관리자 참가신청 화면 등 기본),
 *   'dark' = 게임대회 참가자 대진표 상세 (V70-L 3차 다크 테마). 레이아웃·좌표 계산은 공통.
 */
const LIGHT_PAL = {
  connector: '#94a3b8',
  headerBg: '#f1f5f9', headerText: 'inherit', headerSub: '#64748b',
  cellBg: '#fff', cellShadow: '0 1px 2px rgba(0,0,0,0.04)',
  borderCompleted: '1px solid #86efac', borderBye: '1px dashed #cbd5e1', borderDefault: '1px solid #e5e7eb',
  divider: '#f1f5f9',
  meta: '#64748b', schedule: '#334155', broadcast: '#1e40af',
  mineBg: '#fef3c7', mineBorder: '1px solid #fcd34d', mineIcon: '#92400e',
  nameWinner: '#065f46', name: '#1e293b', scoreWinner: '#065f46', score: '#94a3b8',
  championBg: '#fef3c7', championBorder: '1px solid #fcd34d', championText: '#92400e',
};
const DARK_PAL: typeof LIGHT_PAL = {
  connector: '#5B6475',
  headerBg: DARK.surface, headerText: DARK.text, headerSub: DARK.textMuted,
  cellBg: DARK.card, cellShadow: 'none',
  borderCompleted: '1px solid rgba(34, 197, 94, 0.55)', borderBye: `1px dashed ${DARK.border}`, borderDefault: `1px solid ${DARK.border}`,
  divider: DARK.border,
  meta: DARK.textMuted, schedule: DARK.textMuted, broadcast: '#8CC8FF',
  // 내 참가 강조 — 기존과 같은 노란 계열
  mineBg: 'rgba(245, 158, 11, 0.16)', mineBorder: '1px solid rgba(245, 158, 11, 0.6)', mineIcon: '#FCD34D',
  // 승자 강조 — 기존과 같은 초록 계열
  nameWinner: '#86EFAC', name: DARK.text, scoreWinner: '#86EFAC', score: DARK.textMuted,
  // 우승 배너 — 골드 강조 유지
  championBg: 'rgba(232, 196, 124, 0.12)', championBorder: `1px solid ${DARK.gold}`, championText: '#FCD34D',
};
export type BracketTreeTheme = 'light' | 'dark';

interface Props {
  bracket: PublicBracketView;
  matches: PublicMatchView[];
  myPublicParticipantIds?: string[];
  onMatchClick?: (match: PublicMatchView) => void;
  // V70 QA 추가 수정 #2-a (2026-10-22) — Admin 전용 액션 뱃지 노출.
  showAdminActions?: boolean;
  // V70 QA 신규 기능 #1·#3 (2026-10-22) — Admin 매치 메타 (일정/중계) 설정 진입.
  onMetaClick?: (match: PublicMatchView) => void;
  // V70G8J-2 — 팀전 팀명 → 팀원 이름 팝오버 (참가자용 대진표 상세에서만 켬. Admin 탭은 미사용)
  showTeamMembers?: boolean;
  /** V70-M — 색 테마 (기본 'light' — 관리자 화면 그대로). 참가자 대진표 상세만 'dark' */
  theme?: BracketTreeTheme;
}

export function BracketTreeView({ bracket, matches, myPublicParticipantIds, onMatchClick, showAdminActions, onMetaClick, showTeamMembers, theme = 'light' }: Props) {
  const pal = theme === 'dark' ? DARK_PAL : LIGHT_PAL;
  const partById = new Map<string, PublicParticipantView>();
  for (const p of bracket.participants) partById.set(p.publicParticipantId, p);
  const myIds = new Set(myPublicParticipantIds || []);

  // V70G8J-2 — 팀원 팝오버 (공개 상태 + 팀전 대진표만. 서버도 같은 조건을 다시 검증)
  const teamMembersOn = !!showTeamMembers
    && (bracket.status === 'published' || bracket.status === 'in-progress'
      || bracket.status === 'completed' || bracket.status === 'cancelled')
    && (bracket.participants || []).some((p) => p.mode === 'team');
  const { state: teamMembersState, ensureLoaded: ensureTeamMembers } = useBracketTeamMembers(bracket.bracketId);
  const [openTeam, setOpenTeam] = useState<OpenTeam | null>(null);
  const openTeamRef = useRef<OpenTeam | null>(null);
  openTeamRef.current = openTeam;
  const hoverCloseTimer = useRef<number | null>(null);
  const cancelHoverClose = useCallback(() => {
    if (hoverCloseTimer.current != null) { window.clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null; }
  }, []);
  const scheduleHoverClose = useCallback(() => {
    cancelHoverClose();
    hoverCloseTimer.current = window.setTimeout(() => {
      setOpenTeam((cur) => (cur && !cur.pinned ? null : cur));
    }, 150);
  }, [cancelHoverClose]);
  useEffect(() => () => cancelHoverClose(), [cancelHoverClose]);
  useEffect(() => { setOpenTeam(null); }, [bracket.bracketId]);
  const teamHandlers = React.useMemo(() => ({
    hoverOpen: (pid: string, teamName: string, anchor: HTMLElement) => {
      cancelHoverClose();
      setOpenTeam((cur) => (cur?.pinned ? cur : { publicParticipantId: pid, teamName, anchor, pinned: false }));
      ensureTeamMembers();
    },
    hoverLeave: scheduleHoverClose,
    toggle: (pid: string, teamName: string, anchor: HTMLElement) => {
      cancelHoverClose();
      const cur = openTeamRef.current;
      if (cur && cur.publicParticipantId === pid && cur.pinned) {
        setOpenTeam(null);
        return;
      }
      setOpenTeam({ publicParticipantId: pid, teamName, anchor, pinned: true });
      ensureTeamMembers();
    },
  }), [cancelHoverClose, scheduleHoverClose, ensureTeamMembers]);
  const closeTeam = useCallback((restoreFocus: boolean) => {
    cancelHoverClose();
    const cur = openTeamRef.current;
    setOpenTeam(null);
    if (cur && restoreFocus) cur.anchor.focus();
  }, [cancelHoverClose]);

  // V70 QA 리오픈 수정 #1 (2026-10-22) — 기존 bracket 의 participants[].displayName 이 '-' 또는 공백인 경우
  //   읽기 시점에 getBracketParticipantNames Callable 로 보강. UID 등 추가 식별자 노출 없음.
  //   기존 Firestore doc 은 변경하지 않음. 공개 상태(published/in-progress/completed/cancelled)에만 호출.
  const [enrichedNames, setEnrichedNames] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    const needEnrich = (bracket.participants || []).some((p) => {
      const name = p.mode === 'team' ? (p.teamName || p.displayName) : p.displayName;
      return !name || !name.trim() || name === '-';
    });
    if (!needEnrich) return;
    const openStatus = bracket.status === 'published' || bracket.status === 'in-progress'
      || bracket.status === 'completed' || bracket.status === 'cancelled';
    if (!openStatus) return;
    let cancelled = false;
    (async () => {
      try {
        const fns = getFunctions();
        const r = await httpsCallable<{ bracketId: string }, { byPublicId: Record<string, string> }>(
          fns, 'getBracketParticipantNames'
        )({ bracketId: bracket.bracketId });
        if (!cancelled) setEnrichedNames(r.data.byPublicId || {});
      } catch {
        // 보강 실패 시 기존 값 유지 ('-' 또는 공백)
      }
    })();
    return () => { cancelled = true; };
  }, [bracket.bracketId, bracket.status, bracket.participants]);

  const rounds: PublicMatchView[][] = [];
  for (let r = 1; r <= bracket.totalRounds; r++) {
    const roundMatches = matches
      .filter((m) => m.roundNo === r)
      .sort((a, b) => a.slotIndex - b.slotIndex);
    rounds.push(roundMatches);
  }

  // V70 QA 리오픈 RRRRR1 (5차, 2026-10-23) — **구조적 재설계: Single Source of Truth**.
  //
  //   이전 4차례 수정 (R4 / RR2 / RRR1 / RRRR1) 실패 원인:
  //     - R4: SVG 좌표만 방어 → 레이아웃 자체가 어긋남
  //     - RR2: `space-around` 수학적 가정 → cell 높이 변동 환경 미지원
  //     - RRR1: cells-div-nested absolute positioning + SVG at grid root → **좌표계 2중**
  //     - RRRR1: SVG 좌표만 getBoundingClientRect 로 측정 → cell top 과 SVG line 이 서로 다른
  //       소스 (state cellLayout vs 실측 rect) → 측정/상태 sync 지연 시 어긋남
  //
  //   사용자 지시: "블록 위치와 연결선 좌표를 **단일 레이아웃 모델** 에서 계산."
  //
  //   재설계 (RRRRR1):
  //     1. Grid root 만 position: relative. Column header / cells / SVG 전부 grid root 에 absolute 배치.
  //        → 좌표계 **단일 grid-relative**. 중첩된 cells-div / column wrapper 제거.
  //     2. 고정 COL_WIDTH / COL_GAP / HEADER_H / TOP_PADDING / CELL_GAP 상수 사용.
  //     3. 각 cell 의 absolute `top` + `left` 를 cellLayout 에서 계산.
  //        - R1: HEADER_H + TOP_PADDING + i × (h + CELL_GAP) 자연 스택
  //        - R(n>1): sources center Y 평균 - 자기 h/2
  //        - height 는 측정값 (ResizeObserver 로 동기)
  //     4. SVG connectors 는 **동일 cellLayout** 에서 derive:
  //        - x1 = fromLeft + COL_WIDTH, y1 = fromLay.top + fromLay.h/2
  //        - x2 = toLeft, y2 = toLay.top + toLay.h/2
  //        → cell top 과 SVG y 가 **동일 값** → 반드시 일치.
  //     5. useMemo 로 connectors 를 cellLayout 에서 즉시 derive (별도 state X).
  //     6. 측정 → compute → setCellLayout 1회 사이클 (3-phase rAF 제거).
  //
  //   브라우저 미실행: Playwright E2E 는 add 하되 실행은 사용자 수동 (CI 미구성). 자동 수치 검증 어려움.
  const gridRef = useRef<HTMLDivElement>(null);
  const cellRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  // 레이아웃 상수 — 단일 소스. grid root 기준 (grid-relative).
  const COL_WIDTH = 220;
  const COL_GAP = 16;
  const HEADER_H = 38;           // column header 높이 (padding 포함)
  const TOP_PADDING = 8;         // header → 첫 cell 간 수직 여백
  const CELL_V_GAP = 16;         // R1 cell 간 수직 gap

  // cellLayout: Map<matchId, { top, left, w, h, roundNo }> — 전부 grid-relative.
  const [cellHeights, setCellHeights] = useState<Map<string, number>>(new Map());

  // Phase: cell 높이 측정 → cellHeights 상태 저장
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const measure = () => {
      const newH = new Map<string, number>();
      cellRefs.current.forEach((el, id) => {
        if (el) {
          const h = el.offsetHeight;
          if (h > 0) newH.set(id, h);
        }
      });
      // 변경 사항이 있을 때만 state 업데이트 (무한 루프 방지)
      let changed = newH.size !== cellHeights.size;
      if (!changed) {
        newH.forEach((v, k) => {
          if (cellHeights.get(k) !== v) changed = true;
        });
      }
      if (changed) setCellHeights(newH);
    };
    measure();
    const ro = new ResizeObserver(measure);
    cellRefs.current.forEach((el) => { if (el) ro.observe(el); });
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [matches, bracket.bracketId, cellHeights]);

  // Phase: cellLayout 계산 (useMemo — cellHeights/matches 변경 시 재계산).
  // 좌표는 모두 grid-relative. SVG 와 cell 모두 **동일 값** 사용 → coord 불일치 원천 차단.
  const cellLayout = React.useMemo(() => {
    const EST_H = 100;
    const layout = new Map<string, { top: number; left: number; w: number; h: number; roundNo: number }>();

    // R1: 자연 스택 (top = HEADER_H + TOP_PADDING + accumulated)
    const r1 = matches.filter((m) => m.roundNo === 1).sort((a, b) => a.slotIndex - b.slotIndex);
    let curTop = HEADER_H + TOP_PADDING;
    for (const m of r1) {
      const h = cellHeights.get(m.matchId) ?? EST_H;
      const left = 0 * (COL_WIDTH + COL_GAP);
      layout.set(m.matchId, { top: curTop, left, w: COL_WIDTH, h, roundNo: 1 });
      curTop += h + CELL_V_GAP;
    }

    // R(n>1): sources center Y 평균 - h/2
    for (let r = 2; r <= bracket.totalRounds; r++) {
      const roundMatches = matches.filter((m) => m.roundNo === r).sort((a, b) => a.slotIndex - b.slotIndex);
      for (const m of roundMatches) {
        const sources = matches.filter((sm) => sm.nextMatchId === m.matchId);
        const sourceCenters: number[] = [];
        for (const s of sources) {
          const sLay = layout.get(s.matchId);
          if (sLay) sourceCenters.push(sLay.top + sLay.h / 2);
        }
        const h = cellHeights.get(m.matchId) ?? EST_H;
        const left = (r - 1) * (COL_WIDTH + COL_GAP);
        if (sourceCenters.length === 0) {
          // Fallback: 소스 미확정 시 header 바로 아래 배치
          layout.set(m.matchId, { top: HEADER_H + TOP_PADDING, left, w: COL_WIDTH, h, roundNo: r });
          continue;
        }
        const avgY = sourceCenters.reduce((a, b) => a + b, 0) / sourceCenters.length;
        layout.set(m.matchId, { top: avgY - h / 2, left, w: COL_WIDTH, h, roundNo: r });
      }
    }
    return layout;
  }, [cellHeights, matches, bracket.bracketId, bracket.totalRounds]);

  // Phase: SVG connectors 를 cellLayout 에서 직접 derive (별도 상태 X).
  //   → cell top 과 SVG y 가 **반드시 동일 값** (JS object reference 참조)
  const connectors = React.useMemo(() => {
    const matchIdSet = new Set(matches.map((mm) => mm.matchId));
    const lines: Array<{ d: string; fromId: string; toId: string }> = [];
    for (const m of matches) {
      if (!m.nextMatchId) continue;
      if (m.nextMatchId === m.matchId) continue;
      if (!matchIdSet.has(m.nextMatchId)) continue;
      const from = cellLayout.get(m.matchId);
      const to = cellLayout.get(m.nextMatchId);
      if (!from || !to) continue;
      // 좌표: grid-relative (cell style top/left 와 동일 소스).
      const x1 = from.left + from.w;         // from cell 오른쪽 끝
      const y1 = from.top + from.h / 2;      // from cell 세로 중앙 (SSoT)
      const x2 = to.left;                     // to cell 왼쪽 끝
      const y2 = to.top + to.h / 2;           // to cell 세로 중앙 (SSoT)
      if (x2 <= x1) continue;
      const midX = x1 + (x2 - x1) / 2;
      const round1 = (v: number) => Math.round(v * 10) / 10;
      lines.push({
        d: `M ${round1(x1)} ${round1(y1)} H ${round1(midX)} V ${round1(y2)} H ${round1(x2)}`,
        fromId: m.matchId,
        toId: m.nextMatchId,
      });
    }
    return lines;
  }, [cellLayout, matches]);

  // Phase: canvas 크기 계산 — 전체 cells + header + padding 포함.
  const canvasSize = React.useMemo(() => {
    let maxBottom = HEADER_H + TOP_PADDING;
    cellLayout.forEach((v) => {
      if (v.top + v.h > maxBottom) maxBottom = v.top + v.h;
    });
    const w = bracket.totalRounds * COL_WIDTH + (bracket.totalRounds - 1) * COL_GAP;
    const h = maxBottom + TOP_PADDING;
    return { w, h };
  }, [cellLayout, bracket.totalRounds]);

  // V70G8J — 우승자 표시를 대진표 위로 이동 (게임명 → 우승자 → 대진표). 표시 조건·문구는 기존 그대로.
  //   가로 스크롤 영역 밖에 두어 모바일에서도 화면 폭 기준으로 보인다.
  const championBanner = bracket.status === 'completed' && bracket.championPublicParticipantId ? (() => {
    // V70 QA 리오픈 #6 (2026-10-22) — 팀전 우승 표시는 팀명만, 개인전은 사원 이름.
    //   기존: displayName (팀장 이름) + teamName 괄호 → 사용자 요구 위반.
    const champ = partById.get(bracket.championPublicParticipantId);
    let label: React.ReactNode;
    if (!champ) label = '-';
    else if (champ.mode === 'team') label = <strong>{champ.teamName || '-'}</strong>;
    else {
      const name = (enrichedNames && enrichedNames[bracket.championPublicParticipantId])
        || champ.displayName
        || '-';
      label = <strong>{name}</strong>;
    }
    return (
      <div
        style={{ ...championBannerStyle, background: pal.championBg, border: pal.championBorder, color: pal.championText }}
        data-testid="bracket-champion-banner"
      >🏆 우승: {label}</div>
    );
  })() : null;

  return (
    <>
    {championBanner}
    <div style={outerStyle} data-testid="bracket-tree-scroll">
      {/* V70 QA 리오픈 RRRRR1 (5차, 2026-10-23) — Grid root 가 유일한 좌표계 anchor.
            Header / cells / SVG 전부 grid root 에 position:absolute 로 배치. 중첩 wrapper 없음. */}
      <div
        ref={gridRef}
        style={{
          position: 'relative',
          width: canvasSize.w,
          minHeight: canvasSize.h,
          minWidth: 'min-content',
        }}
        data-bracket-id={bracket.bracketId}
      >
        {/* SVG connectors — grid root 에 anchored. 좌표는 cellLayout 과 동일 (SSoT). */}
        {connectors.length > 0 && canvasSize.w > 0 && canvasSize.h > 0 && (
          <svg
            width={canvasSize.w}
            height={canvasSize.h}
            viewBox={`0 0 ${canvasSize.w} ${canvasSize.h}`}
            preserveAspectRatio="none"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              pointerEvents: 'none',
              zIndex: 0,
              overflow: 'visible',
            }}
            data-testid="bracket-connectors"
          >
            {connectors.map((c, i) => (
              <path
                key={i}
                d={c.d}
                stroke={pal.connector}
                strokeWidth={2}
                fill="none"
                data-from={c.fromId}
                data-to={c.toId}
              />
            ))}
          </svg>
        )}

        {/* Column headers — grid root 에 absolute, 각 column 의 x 는 (idx × COL_W+GAP). */}
        {rounds.map((_, idx) => {
          const r = idx + 1;
          const cfg = bracket.roundConfig.find((c) => c.roundNo === r);
          const label = cfg ? roundLabelOf(cfg, bracket.totalRounds) : `R${r}`;
          const left = idx * (COL_WIDTH + COL_GAP);
          return (
            <div
              key={`hdr-${r}`}
              style={{
                position: 'absolute',
                top: 0,
                left,
                width: COL_WIDTH,
                height: HEADER_H,
                ...colHeaderStyle,
                background: pal.headerBg,
                color: pal.headerText,
                zIndex: 1,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <strong>{label}</strong>
              <span style={{ fontSize: 11, color: pal.headerSub, marginLeft: 6 }}>
                {cfg?.bestOf ?? 1}판 {Math.ceil((cfg?.bestOf ?? 1) / 2)}승제
              </span>
            </div>
          );
        })}

        {/* Match cells — grid root 에 absolute, 각 cell 의 top/left 는 cellLayout 에서. */}
        {matches.map((m) => {
          const lay = cellLayout.get(m.matchId);
          if (!lay) return null;
          return (
            <div
              key={m.matchId}
              style={{
                position: 'absolute',
                top: lay.top,
                left: lay.left,
                width: lay.w,
                zIndex: 1,
              }}
              data-match-id={m.matchId}
              data-round-no={m.roundNo}
              data-slot-index={m.slotIndex}
            >
              <MatchCell
                match={m}
                partById={partById}
                myIds={myIds}
                enrichedNames={enrichedNames}
                showAdminActions={showAdminActions}
                teamMembers={teamMembersOn ? { openPid: openTeam?.publicParticipantId ?? null, ...teamHandlers } : undefined}
                onClick={onMatchClick ? () => onMatchClick(m) : undefined}
                onMetaClick={onMetaClick ? () => onMetaClick(m) : undefined}
                cellRef={(el) => { cellRefs.current.set(m.matchId, el); }}
                pal={pal}
              />
            </div>
          );
        })}
      </div>
    </div>
    {teamMembersOn && openTeam && (
      <TeamMembersPopover
        theme={theme}
        open={openTeam}
        state={teamMembersState}
        onClose={closeTeam}
        onPopoverEnter={cancelHoverClose}
        onPopoverLeave={scheduleHoverClose}
      />
    )}
    </>
  );
}

interface TeamMembersCellProps {
  openPid: string | null;
  hoverOpen: (pid: string, teamName: string, anchor: HTMLElement) => void;
  hoverLeave: () => void;
  toggle: (pid: string, teamName: string, anchor: HTMLElement) => void;
}

function MatchCell({
  match, partById, myIds, onClick, enrichedNames, showAdminActions, onMetaClick, cellRef, teamMembers, pal = LIGHT_PAL,
}: {
  pal?: typeof LIGHT_PAL;
  match: PublicMatchView;
  partById: Map<string, PublicParticipantView>;
  myIds: Set<string>;
  onClick?: () => void;
  enrichedNames?: Record<string, string> | null;
  showAdminActions?: boolean;
  onMetaClick?: () => void;
  cellRef?: (el: HTMLDivElement | null) => void;
  teamMembers?: TeamMembersCellProps;
}) {
  // V70G8J-2 — 팀전 참가자 이름만 팀원 팝오버 트리거로 감싼다 (개인전·부전승·진출 대기는 그대로 텍스트)
  const nameNode = (p: PublicParticipantView | null | undefined, fallback: string): React.ReactNode => {
    if (!p) return fallback;
    const label = displayLabel(p, enrichedNames);
    if (!teamMembers || p.mode !== 'team') return label;
    const pid = p.publicParticipantId;
    return (
      <TeamNameTrigger
        publicParticipantId={pid}
        teamName={label}
        open={teamMembers.openPid === pid}
        onHoverOpen={(el) => teamMembers.hoverOpen(pid, label, el)}
        onHoverLeave={teamMembers.hoverLeave}
        onToggle={(el) => teamMembers.toggle(pid, label, el)}
      >
        {label}
      </TeamNameTrigger>
    );
  };
  const pa = match.playerA.publicParticipantId ? partById.get(match.playerA.publicParticipantId) : null;
  const pb = match.playerB.publicParticipantId ? partById.get(match.playerB.publicParticipantId) : null;
  const aMine = !!(match.playerA.publicParticipantId && myIds.has(match.playerA.publicParticipantId));
  const bMine = !!(match.playerB.publicParticipantId && myIds.has(match.playerB.publicParticipantId));
  // V70 QA 리오픈 #11 (2026-10-22) — 승자 강조 조건:
  //   1) 매치 status 가 'completed' 또는 'bye' 여야 함 (경기 진행 전/중에는 강조 금지)
  //   2) match.winnerSide 와 각 side 가 **정확히** 매치해야 함
  //   3) BYE 매치는 자동 승리 처리됨 (실제 참가자 있는 쪽만 강조)
  //   이전엔 status 체크 없이 `match.winnerSide === 'A'` 만 → 'ready'/'in-progress' 상태라도 저장된 winnerSide 가 있으면 강조.
  const winnerFinalized = match.status === 'completed' || match.status === 'bye';
  const aWinner = winnerFinalized && match.winnerSide === 'A';
  const bWinner = winnerFinalized && match.winnerSide === 'B';
  const setsA = match.sets.filter((s) => s.winnerSide === 'A').length;
  const setsB = match.sets.filter((s) => s.winnerSide === 'B').length;

  // V70 QA 추가 수정 #2-a (2026-10-22) — Admin 모드에서 완료 매치도 클릭 가능 (결과 정정).
  const clickable = !!onClick && (
    match.status === 'ready' || match.status === 'in-progress' ||
    (!!showAdminActions && match.status === 'completed')
  );

  return (
    <div
      ref={cellRef}
      style={{
        ...cellStyle,
        background: pal.cellBg,
        boxShadow: pal.cellShadow,
        border: match.status === 'completed' ? pal.borderCompleted
              : match.status === 'bye' ? pal.borderBye
              : pal.borderDefault,
        cursor: clickable ? 'pointer' : 'default',
        position: 'relative',
        zIndex: 1,
      }}
      onClick={clickable ? onClick : undefined}
    >
      <SideRow name={nameNode(pa, match.playerA.sourceMatchId ? '(진출 대기)' : '부전승')}
               winner={aWinner} mine={aMine} score={setsA} pal={pal} />
      <div style={{ height: 1, background: pal.divider, margin: '2px 0' }} />
      <SideRow name={nameNode(pb, match.playerB.sourceMatchId ? '(진출 대기)' : '부전승')}
               winner={bWinner} mine={bMine} score={setsB} pal={pal} />
      {match.status === 'completed' && match.completedAt && (
        <div style={{ fontSize: 10, color: pal.meta, marginTop: 2 }}>
          완료 {new Date(tsToMs(match.completedAt)).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
        </div>
      )}
      {/* V70 QA 신규 기능 #1 (2026-10-22) — 매치 일정 표시 (단일 or 기간). */}
      {match.schedule && (
        <div style={{ fontSize: 10, color: pal.schedule, marginTop: 2 }}>
          📅 {match.schedule.kind === 'range' && match.schedule.endDate
            ? `${match.schedule.startDate} ~ ${match.schedule.endDate}`
            : match.schedule.startDate}
        </div>
      )}
      {/* V70 QA 신규 기능 #3 (2026-10-22) — 매치 중계 정보 표시 (enabled=true 때만). */}
      {match.broadcast && match.broadcast.enabled && (
        <div style={{ fontSize: 10, color: pal.broadcast, marginTop: 2 }}>
          🎥 {new Date(tsToMs(match.broadcast.startAt)).toLocaleString('ko-KR', {
            timeZone: 'Asia/Seoul', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
          {' @'}{match.broadcast.location}
        </div>
      )}
      {/* V70 QA 추가 수정 #2-a (2026-10-22) — Admin 전용 액션 뱃지 (명시적 진입 경로). */}
      {showAdminActions && (match.status === 'ready' || match.status === 'in-progress') && (
        <div style={adminActionBadge}>+ 세트 입력</div>
      )}
      {showAdminActions && match.status === 'completed' && (
        <div style={{ ...adminActionBadge, background: '#fef3c7', color: '#92400e' }}>✎ 결과 정정</div>
      )}
      {/* V70 QA 신규 기능 #1·#3 (2026-10-22) — Admin 전용 매치 메타(일정/중계) 설정 버튼. */}
      {showAdminActions && onMetaClick && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMetaClick(); }}
          style={{
            marginTop: 4, padding: '2px 6px', fontSize: 10, fontWeight: 600,
            background: '#e0e7ff', color: '#3730a3', border: '1px solid #c7d2fe', borderRadius: 4,
            cursor: 'pointer', width: '100%',
          }}
        >⚙ 일정/중계 설정</button>
      )}
    </div>
  );
}

function displayLabel(p: PublicParticipantView, enrichedNames?: Record<string, string> | null): string {
  if (p.mode === 'team' && p.teamName && p.teamName.trim()) return p.teamName;
  // V70 QA 리오픈 R2 (2026-10-22) — Admin 저장 원형(BracketParticipantSnapshot)은 ownerDisplayName 사용,
  //   PublicParticipant 변환 시만 displayName 생성. BracketTreeView 가 Admin bracket 받으면
  //   p.displayName 이 undefined → '-' 표시 버그. 두 필드 fallback 체인:
  //   1) displayName (PublicParticipant)
  //   2) ownerDisplayName (BracketParticipantSnapshot, Admin fetchBracket 응답)
  //   3) enrichedNames (공개 상태 bracket 에서 getBracketParticipantNames 보강)
  //   4) '-' 최종 fallback.
  const anyP = p as any;
  const displayCand = p.displayName || anyP.ownerDisplayName;
  const stored = displayCand && String(displayCand).trim() && displayCand !== '-' ? String(displayCand).trim() : null;
  if (stored) return stored;
  const enriched = enrichedNames ? enrichedNames[p.publicParticipantId] : null;
  if (enriched && enriched.trim() && enriched !== '-') return enriched;
  return p.displayName || '-';
}

function SideRow({ name, winner, mine, score, pal = LIGHT_PAL }: { name: React.ReactNode; winner: boolean; mine: boolean; score: number; pal?: typeof LIGHT_PAL }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '4px 8px', borderRadius: 4,
      background: mine ? pal.mineBg : 'transparent',
      border: mine ? pal.mineBorder : '1px solid transparent',
    }}>
      <span style={{
        fontSize: 12,
        fontWeight: winner ? 700 : 500,
        color: winner ? pal.nameWinner : pal.name,
        maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {mine && <span style={{ color: pal.mineIcon, marginRight: 4 }}>👤</span>}
        {name}
      </span>
      <span style={{ fontSize: 12, fontWeight: 700, color: winner ? pal.scoreWinner : pal.score }}>{score}</span>
    </div>
  );
}

const outerStyle: React.CSSProperties = {
  width: '100%', overflowX: 'auto', paddingBottom: 10,
};
const gridStyle: React.CSSProperties = {
  // V70 QA 리오픈 RRR1 재재재수정 (2026-10-23) — flex-start 로 복원. cells 는 absolute positioning 이라 column 자체 stretch 불필요.
  display: 'flex', gap: 16, alignItems: 'flex-start', minWidth: 'min-content',
};
const colStyle: React.CSSProperties = {
  minWidth: 220, display: 'flex', flexDirection: 'column',
};
const colHeaderStyle: React.CSSProperties = {
  fontSize: 13, padding: '6px 10px', background: '#f1f5f9', borderRadius: 6, marginBottom: 8,
};
const cellStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 6, padding: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};
const adminActionBadge: React.CSSProperties = {
  marginTop: 4,
  padding: '2px 6px',
  background: '#dbeafe',
  color: '#1e40af',
  borderRadius: 4,
  fontSize: 10,
  fontWeight: 600,
  textAlign: 'center',
};
const championBannerStyle: React.CSSProperties = {
  marginBottom: 12, padding: '12px 16px', background: '#fef3c7', border: '1px solid #fcd34d',
  borderRadius: 8, color: '#92400e', fontSize: 14, textAlign: 'center',
  overflowWrap: 'anywhere', boxSizing: 'border-box',
};
